// src/pages/finance-portal/dimensions/Dimensions.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Card, CardContent,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, IconButton, Alert, CircularProgress, Skeleton,
    Dialog, DialogTitle, DialogContent, DialogActions,
    FormControlLabel, Switch, Checkbox, Collapse, Tooltip,
    MenuItem, Select, FormControl, InputLabel,
} from '@mui/material';
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const emptyDimension = { name: '', code: '', is_required: false, is_active: true };
const emptyValue = { name: '', code: '', parent_id: '', is_active: true };

export default function Dimensions() {
    const [dimensions, setDimensions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Expanded dimension IDs
    const [expanded, setExpanded] = useState({});

    // Dimension values keyed by dimension id
    const [valuesMap, setValuesMap] = useState({});
    const [valuesLoading, setValuesLoading] = useState({});

    // Dimension dialog
    const [dimDialog, setDimDialog] = useState({ open: false, editing: null });
    const [dimForm, setDimForm] = useState(emptyDimension);
    const [dimErrors, setDimErrors] = useState({});
    const [dimSaving, setDimSaving] = useState(false);

    // Value dialog
    const [valDialog, setValDialog] = useState({ open: false, editing: null, dimensionId: null });
    const [valForm, setValForm] = useState(emptyValue);
    const [valErrors, setValErrors] = useState({});
    const [valSaving, setValSaving] = useState(false);

    // Delete dialog
    const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, item: null, dimensionId: null });
    const [deleting, setDeleting] = useState(false);

    const fetchDimensions = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get('/api/finance/dimensions');
            if (res.data.success) {
                setDimensions(res.data.data);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load dimensions');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { fetchDimensions(); }, [fetchDimensions]);

    const fetchValues = useCallback(async (dimensionId) => {
        setValuesLoading(prev => ({ ...prev, [dimensionId]: true }));
        try {
            const res = await axiosInstance.get(`/api/finance/dimensions/${dimensionId}/values`);
            if (res.data.success) {
                setValuesMap(prev => ({ ...prev, [dimensionId]: res.data.data }));
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load dimension values');
        } finally {
            setValuesLoading(prev => ({ ...prev, [dimensionId]: false }));
        }
    }, []);

    const toggleExpand = (dimensionId) => {
        const isExpanded = expanded[dimensionId];
        setExpanded(prev => ({ ...prev, [dimensionId]: !isExpanded }));
        if (!isExpanded && !valuesMap[dimensionId]) {
            fetchValues(dimensionId);
        }
    };

    // Toggle dimension active
    const handleToggleActive = async (dim) => {
        try {
            await axiosInstance.put(`/api/finance/dimensions/${dim.id}`, { ...dim, is_active: !dim.is_active });
            fetchDimensions();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update dimension');
        }
    };

    // Dimension CRUD
    const openDimDialog = (dim = null) => {
        setDimForm(dim ? { name: dim.name, code: dim.code, is_required: dim.is_required, is_active: dim.is_active } : emptyDimension);
        setDimDialog({ open: true, editing: dim });
    };

    const saveDimension = async () => {
        setDimSaving(true);
        try {
            if (dimDialog.editing) {
                await axiosInstance.put(`/api/finance/dimensions/${dimDialog.editing.id}`, dimForm);
                setSuccess('Dimension updated');
            } else {
                await axiosInstance.post('/api/finance/dimensions', dimForm);
                setSuccess('Dimension created');
            }
            setDimDialog({ open: false, editing: null });
            setDimErrors({});
            fetchDimensions();
        } catch (err) {
            const serverErrors = err.response?.data?.errors;
            const msg = err.response?.data?.message || 'Failed to save dimension';
            if (serverErrors && typeof serverErrors === 'object') {
                setDimErrors(serverErrors);
            }
            setError(msg);
        } finally {
            setDimSaving(false);
        }
    };

    // Value CRUD
    const openValDialog = (dimensionId, val = null) => {
        setValForm(val ? { name: val.name, code: val.code, parent_id: val.parent_id || '', is_active: val.is_active } : emptyValue);
        setValDialog({ open: true, editing: val, dimensionId });
    };

    const saveValue = async () => {
        setValSaving(true);
        const payload = { ...valForm, parent_id: valForm.parent_id || null };
        try {
            if (valDialog.editing) {
                await axiosInstance.put(`/api/finance/dimension-values/${valDialog.editing.id}`, payload);
                setSuccess('Value updated');
            } else {
                await axiosInstance.post(`/api/finance/dimensions/${valDialog.dimensionId}/values`, payload);
                setSuccess('Value created');
            }
            setValDialog({ open: false, editing: null, dimensionId: null });
            setValErrors({});
            fetchValues(valDialog.dimensionId);
        } catch (err) {
            const serverErrors = err.response?.data?.errors;
            const msg = err.response?.data?.message || 'Failed to save value';
            if (serverErrors && typeof serverErrors === 'object') {
                setValErrors(serverErrors);
            }
            setError(msg);
        } finally {
            setValSaving(false);
        }
    };

    // Delete
    const confirmDelete = async () => {
        setDeleting(true);
        try {
            if (deleteDialog.type === 'dimension') {
                await axiosInstance.delete(`/api/finance/dimensions/${deleteDialog.item.id}`);
                setSuccess('Dimension deleted');
                fetchDimensions();
            } else {
                await axiosInstance.delete(`/api/finance/dimension-values/${deleteDialog.item.id}`);
                setSuccess('Value deleted');
                fetchValues(deleteDialog.dimensionId);
            }
            setDeleteDialog({ open: false, type: null, item: null, dimensionId: null });
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <ViewInArIcon sx={{ fontSize: 28, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>Dimensions</Typography>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>Multi-dimensional tracking</Typography>
                        </Box>
                    </Box>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => openDimDialog()}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.2)', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }, color: '#fff', boxShadow: 'none' }}
                    >
                        Add Dimension
                    </Button>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}
                {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{success}</Alert>}

                {isLoading ? (
                    [1, 2, 3].map(i => <Skeleton key={i} height={80} sx={{ borderRadius: '16px', mb: 2 }} />)
                ) : dimensions.length === 0 ? (
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 4, textAlign: 'center' }}>
                        <ViewInArIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                        <Typography sx={{ fontFamily, color: '#94a3b8' }}>No dimensions created yet</Typography>
                    </Card>
                ) : dimensions.map(dim => (
                    <Card key={dim.id} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 2, overflow: 'visible' }}>
                        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                            {/* Dimension Header Row */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2.5, py: 2, cursor: 'pointer', '&:hover': { bgcolor: '#f8fafc' }, transition: 'background 0.15s' }}
                                onClick={() => toggleExpand(dim.id)}
                            >
                                <IconButton size="small" sx={{ p: 0.25 }}>
                                    {expanded[dim.id] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                </IconButton>
                                <Box sx={{ flex: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>{dim.name}</Typography>
                                        <Chip label={dim.code} size="small" sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22, bgcolor: `${ACCENT}15`, color: ACCENT }} />
                                        {dim.is_required && (
                                            <Chip label="Required" size="small" sx={{ fontFamily, fontSize: '0.65rem', fontWeight: 600, height: 20, bgcolor: '#fef3c7', color: '#92400e' }} />
                                        )}
                                    </Box>
                                </Box>
                                <FormControlLabel
                                    control={
                                        <Switch checked={dim.is_active} size="small"
                                            onChange={(e) => { e.stopPropagation(); handleToggleActive(dim); }}
                                            onClick={(e) => e.stopPropagation()}
                                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT } }}
                                        />
                                    }
                                    label={<Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b' }}>{dim.is_active ? 'Active' : 'Inactive'}</Typography>}
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{ mr: 0 }}
                                />
                                <Tooltip title="Edit Dimension">
                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); openDimDialog(dim); }} sx={{ color: '#94a3b8', '&:hover': { color: '#3b82f6' } }}>
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete Dimension">
                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setDeleteDialog({ open: true, type: 'dimension', item: dim, dimensionId: null }); }} sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>

                            {/* Expanded Values Section */}
                            <Collapse in={!!expanded[dim.id]} timeout={200}>
                                <Box sx={{ borderTop: '1px solid #e2e8f0', px: 2.5, py: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                                        <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#64748b' }}>Dimension Values</Typography>
                                        <Button size="small" startIcon={<AddIcon />} onClick={() => openValDialog(dim.id)}
                                            sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '8px', color: ACCENT, fontSize: '0.8rem' }}
                                        >
                                            Add Value
                                        </Button>
                                    </Box>

                                    {valuesLoading[dim.id] ? (
                                        [1, 2].map(i => <Skeleton key={i} height={36} sx={{ borderRadius: '8px' }} />)
                                    ) : !valuesMap[dim.id] || valuesMap[dim.id].length === 0 ? (
                                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#94a3b8', textAlign: 'center', py: 2 }}>No values yet</Typography>
                                    ) : (
                                        <TableContainer>
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                                        {['Name', 'Code', 'Parent', 'Active', 'Actions'].map(h => (
                                                            <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0' }}>{h}</TableCell>
                                                        ))}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {valuesMap[dim.id].map(val => (
                                                        <TableRow key={val.id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>{val.name}</TableCell>
                                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{val.code}</TableCell>
                                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{val.parent_name || '—'}</TableCell>
                                                            <TableCell>
                                                                <Chip label={val.is_active ? 'Active' : 'Inactive'} size="small"
                                                                    sx={{ fontFamily, fontSize: '0.65rem', fontWeight: 600, height: 20, bgcolor: val.is_active ? '#dcfce7' : '#f1f5f9', color: val.is_active ? '#166534' : '#64748b' }}
                                                                />
                                                            </TableCell>
                                                            <TableCell>
                                                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                                    <Tooltip title="Edit"><IconButton size="small" onClick={() => openValDialog(dim.id, val)} sx={{ color: '#94a3b8', '&:hover': { color: '#3b82f6' } }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                                                    <Tooltip title="Delete"><IconButton size="small" onClick={() => setDeleteDialog({ open: true, type: 'value', item: val, dimensionId: dim.id })} sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444' } }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                                                </Box>
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </Box>
                            </Collapse>
                        </CardContent>
                    </Card>
                ))}
            </Box>

            {/* Dimension Dialog */}
            <Dialog open={dimDialog.open} onClose={() => setDimDialog({ open: false, editing: null })} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: '16px', fontFamily } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem' }}>
                    {dimDialog.editing ? 'Edit Dimension' : 'Add Dimension'}
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
                    <TextField label="Name" value={dimForm.name} onChange={e => setDimForm(prev => ({ ...prev, name: e.target.value }))}
                        fullWidth size="small" InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} InputLabelProps={{ sx: { fontFamily } }}
                    />
                    <TextField label="Code" value={dimForm.code} onChange={e => setDimForm(prev => ({ ...prev, code: e.target.value }))}
                        fullWidth size="small" InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} InputLabelProps={{ sx: { fontFamily } }}
                    />
                    <FormControlLabel
                        control={<Checkbox checked={dimForm.is_required} onChange={e => setDimForm(prev => ({ ...prev, is_required: e.target.checked }))} sx={{ color: ACCENT, '&.Mui-checked': { color: ACCENT } }} />}
                        label={<Typography sx={{ fontFamily, fontSize: '0.9rem' }}>Required on journal entries</Typography>}
                    />
                    <FormControlLabel
                        control={<Switch checked={dimForm.is_active} onChange={e => setDimForm(prev => ({ ...prev, is_active: e.target.checked }))}
                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT } }}
                        />}
                        label={<Typography sx={{ fontFamily, fontSize: '0.9rem' }}>Active</Typography>}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setDimDialog({ open: false, editing: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={saveDimension} disabled={dimSaving || !dimForm.name || !dimForm.code}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                    >
                        {dimSaving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : dimDialog.editing ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Value Dialog */}
            <Dialog open={valDialog.open} onClose={() => setValDialog({ open: false, editing: null, dimensionId: null })} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: '16px', fontFamily } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem' }}>
                    {valDialog.editing ? 'Edit Value' : 'Add Value'}
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
                    <TextField label="Name" value={valForm.name} onChange={e => setValForm(prev => ({ ...prev, name: e.target.value }))}
                        fullWidth size="small" InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} InputLabelProps={{ sx: { fontFamily } }}
                    />
                    <TextField label="Code" value={valForm.code} onChange={e => setValForm(prev => ({ ...prev, code: e.target.value }))}
                        fullWidth size="small" InputProps={{ sx: { fontFamily, borderRadius: '10px' } }} InputLabelProps={{ sx: { fontFamily } }}
                    />
                    <FormControl fullWidth size="small">
                        <InputLabel sx={{ fontFamily }}>Parent Value</InputLabel>
                        <Select value={valForm.parent_id} onChange={e => setValForm(prev => ({ ...prev, parent_id: e.target.value }))}
                            label="Parent Value" sx={{ fontFamily, borderRadius: '10px' }}
                        >
                            <MenuItem value="">None</MenuItem>
                            {(valuesMap[valDialog.dimensionId] || [])
                                .filter(v => !valDialog.editing || v.id !== valDialog.editing.id)
                                .map(v => <MenuItem key={v.id} value={v.id} sx={{ fontFamily }}>{v.name} ({v.code})</MenuItem>)
                            }
                        </Select>
                    </FormControl>
                    <FormControlLabel
                        control={<Switch checked={valForm.is_active} onChange={e => setValForm(prev => ({ ...prev, is_active: e.target.checked }))}
                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: ACCENT }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: ACCENT } }}
                        />}
                        label={<Typography sx={{ fontFamily, fontSize: '0.9rem' }}>Active</Typography>}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setValDialog({ open: false, editing: null, dimensionId: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={saveValue} disabled={valSaving || !valForm.name || !valForm.code}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                    >
                        {valSaving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : valDialog.editing ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, type: null, item: null, dimensionId: null })}
                PaperProps={{ sx: { borderRadius: '16px', fontFamily } }}
            >
                <DialogTitle sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem' }}>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#475569' }}>
                        Are you sure you want to delete {deleteDialog.type === 'dimension' ? 'this dimension and all its values' : 'this value'}?
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setDeleteDialog({ open: false, type: null, item: null, dimensionId: null })} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button variant="contained" onClick={confirmDelete} disabled={deleting}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}
                    >
                        {deleting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
